import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/i18n/translations";
import SiteHeader from "@/components/SiteHeader";
import Footer from "@/components/Footer";

/**
 * Beautiful B&W 404 page with subtle animations.
 *
 * Animations:
 *  - "404" digits float in with a typewriter-style stagger + glitch wobble
 *  - Subtle continuous breathing on the 0
 *  - Cursor blinking after the headline
 *  - Hover-shimmer on CTAs
 */
export default function NotFoundPage({ context }) {
  const navigate = useNavigate();
  const params = useParams();
  const { t } = useTranslation();
  const [mounted, setMounted] = useState(false);
  const searchedPath = context?.searchedSlug || params.slug || window.location.pathname.slice(1);

  useEffect(() => {
    const tm = setTimeout(() => setMounted(true), 60);
    return () => clearTimeout(tm);
  }, []);

  return (
    <>
      <Helmet>
        <title>404 — {t("notFound.title") || "Page not found"} | Chatity</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <style>{`
        @keyframes nf-drop {
          0%   { transform: translateY(-40px) rotate(-2deg); opacity: 0; }
          70%  { transform: translateY(8px)  rotate(1deg);  opacity: 1; }
          100% { transform: translateY(0)    rotate(0);     opacity: 1; }
        }
        @keyframes nf-breathe {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.06); }
        }
        @keyframes nf-cursor {
          0%, 49%  { opacity: 1; }
          50%, 100% { opacity: 0; }
        }
        @keyframes nf-glitch {
          0%, 100% { text-shadow: none; }
          20%      { text-shadow: 2px 0 #888, -2px 0 #ccc; }
          40%      { text-shadow: -2px 0 #888, 2px 0 #ccc; }
          60%      { text-shadow: 1px 1px #999; }
        }
        @keyframes nf-fade-up {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .nf-digit { display: inline-block; animation: nf-drop 0.6s cubic-bezier(.34,1.56,.64,1) both; }
        .nf-digit-0 { animation: nf-drop 0.6s cubic-bezier(.34,1.56,.64,1) both, nf-breathe 3.5s ease-in-out 1s infinite; }
        .nf-glitch:hover { animation: nf-glitch 0.3s steps(2) 2; }
        .nf-cursor::after { content: "_"; animation: nf-cursor 1.1s steps(1) infinite; margin-left: 4px; color: #000; }
        .nf-fade { opacity: 0; animation: nf-fade-up 0.5s ease-out forwards; }
      `}</style>

      <div className="min-h-screen flex flex-col bg-white text-black">
        <SiteHeader />

        <main className="flex-1 flex items-center justify-center px-4 pt-28 pb-16">
          <div className="max-w-3xl w-full text-center">
            {/* Animated 404 digits */}
            <h1
              className="font-black tracking-tighter leading-none nf-glitch select-none"
              style={{ fontSize: "clamp(8rem, 22vw, 18rem)" }}
              data-testid="notfound-title"
              aria-label="404"
            >
              <span className="nf-digit" style={{ animationDelay: "0.05s" }}>4</span>
              <span className="nf-digit nf-digit-0" style={{ animationDelay: "0.2s" }}>0</span>
              <span className="nf-digit" style={{ animationDelay: "0.35s" }}>4</span>
            </h1>

            <div
              className="nf-fade mt-4 text-2xl sm:text-3xl font-bold nf-cursor inline-block"
              style={{ animationDelay: "0.6s" }}
            >
              {t("notFound.headline") || "Эта страница потерялась"}
            </div>

            <p
              className="nf-fade mt-4 text-base sm:text-lg text-gray-600 max-w-xl mx-auto"
              style={{ animationDelay: "0.8s" }}
            >
              {t("notFound.subtitle") ||
                "Возможно, ссылка устарела или с опечаткой. Но AI Chatity никогда не теряется 😉"}
            </p>

            {searchedPath && (
              <code
                className="nf-fade mt-5 inline-block bg-gray-100 border-2 border-black px-3 py-1.5 rounded text-sm font-mono"
                style={{ animationDelay: "0.9s" }}
                data-testid="notfound-searched-path"
              >
                /{searchedPath}
              </code>
            )}

            {/* Main CTAs */}
            <div
              className="nf-fade mt-10 flex flex-col sm:flex-row gap-3 justify-center"
              style={{ animationDelay: "1.0s" }}
            >
              <Button
                onClick={() => navigate("/")}
                size="lg"
                className="bg-black text-white hover:bg-gray-800 border-2 border-black px-8 py-6 text-base font-bold"
                data-testid="notfound-home-btn"
              >
                🏠 {t("notFound.cta.home") || "На главную"}
              </Button>
              <Button
                onClick={() => navigate("/widget-demo/demo_landing?open=1")}
                size="lg"
                variant="outline"
                className="border-2 border-black text-black hover:bg-black hover:text-white px-8 py-6 text-base font-bold"
                data-testid="notfound-demo-btn"
              >
                💬 {t("notFound.cta.demo") || "Попробовать AI"}
              </Button>
            </div>

            {/* Secondary helpful links */}
            <div
              className="nf-fade mt-12 pt-8 border-t-2 border-gray-200"
              style={{ animationDelay: "1.2s" }}
            >
              <p className="text-sm text-gray-500 mb-4 uppercase tracking-widest">
                {t("notFound.helpfulLinks") || "Или попробуйте"}
              </p>
              <div className="flex flex-wrap justify-center gap-x-6 gap-y-3 text-sm font-medium">
                <Link to="/blog" className="hover:underline underline-offset-4 decoration-2">
                  📰 {t("notFound.link.blog") || "Блог"}
                </Link>
                <Link to="/partner" className="hover:underline underline-offset-4 decoration-2">
                  💼 {t("notFound.link.partner") || "Стать партнёром"}
                </Link>
                <Link to="/auth" className="hover:underline underline-offset-4 decoration-2">
                  🔑 {t("notFound.link.login") || "Войти"}
                </Link>
                <a
                  href="mailto:support@chatity.io"
                  className="hover:underline underline-offset-4 decoration-2"
                >
                  📞 {t("notFound.link.support") || "Поддержка"}
                </a>
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}
